import { array, hash } from '@ember/helper';
import { service } from '@ember/service';

import CpPanel from 'ember-collapsible-panel/components/cp-panel/component';
import { t } from 'ember-intl';
import momentFormat from 'ember-moment/helpers/moment-format';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

import Component from 'umg-ember-backend/components/record-detail/forms';
import AssetPreview from 'umg-ember-backend/components/ui/asset-preview';
import Form from 'umg-ember-backend/components/ui/form';
import InfoBox from 'umg-ember-backend/components/ui/info-box';

const join = (arr) => arr && arr.length && arr.join(' / ');

export default class RecordDetailFormsProductFamilyComponent extends Component {
  // Services
  @service data;

  // Template
  <template>
    <Form @model={{@model}} @showErrors={{@showErrors}} as |form|>
      <div class='o-layout'>
        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          <form.sections.variant
            @availableLanguages={{this.availableLanguages}}
          />

          <form.fieldset @titleKey='general' as |fieldset|>
            <fieldset.field
              @property='cmsState'
              @type='select-value'
              @options={{this.cmsStateOptions}}
            />

            {{#if this.isParent}}
              <fieldset.field
                @property='cmsLanguageId'
                @type='select-value'
                @options={{this.availableLanguages}}
              />
            {{/if}}

            <fieldset.field @property='alphaId' />

            {{#if @model.showAlphaInformation}}
              <InfoBox @type='warning'>
                <strong>{{t
                    'record_detail.forms.product-family.alphaInformationText'
                  }}</strong><br /><br />
                <strong>{{t
                    'record_detail.forms.product-family.alphaInformation.productFamily'
                  }}:<br /></strong>
                {{@model.alphaInformation.title}}<br />
                <strong>{{t
                    'record_detail.forms.product-family.alphaInformation.artists'
                  }}:</strong>
                {{#each @model.alphaInformation.artists as |item|}}
                  <br />{{item}}
                {{/each}}
                <br />
                <strong>{{t
                    'record_detail.forms.product-family.alphaInformation.projectName'
                  }}:<br /></strong>
                {{@model.alphaInformation.projectName}}
                <br />
                <strong>{{t
                    'record_detail.forms.product-family.alphaInformation.originalProducts'
                  }}:<br /></strong>
                {{join @model.alphaInformation.originalProducts}}<br />
                <a
                  href='https://alpha.umusic.net/projects/{{@model.alphaInformation.projectId}}'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {{t
                    'record_detail.forms.product-family.alphaInformation.linkText'
                  }}
                </a>
              </InfoBox>
            {{/if}}

            <fieldset.field @property='headline' />

            <fieldset.field @property='alternativeHeadline' />

            <fieldset.field
              @property='alternativeSubHeadlines'
              @type='text-editor'
              @textEditorPreset='blank'
            />

            <fieldset.field
              @property='repertoireDescription'
              @type='text-editor'
              @textEditorPreset='blank'
            />

            <fieldset.field
              @property='additionalDescription'
              @type='text-editor'
              @textEditorPreset='blank'
            />

            <fieldset.field
              @property='artistDescription'
              @type='text-editor'
              @textEditorPreset='blank'
            />

            <fieldset.field
              @property='additionalProductInfo'
              @type='text-editor'
              @textEditorPreset='blank'
            />

            <fieldset.field @property='color' @type='color' />
          </form.fieldset>

          <form.fieldset @titleKey='keywords' as |fieldset|>
            <fieldset.field @property='keywords' />
          </form.fieldset>

          <form.sections.seo />
        </div>

        <div class='o-layout__item u-1/1 u-1/2@tablet'>
          <form.fieldset @titleKey='contributors' as |fieldset|>
            <fieldset.field
              @property='contributors'
              @type='has-many-join'
              @hasManyForeignModelName='artist'
              @on={{hash openNewRecord=false removeRecord=this.deleteRelation}}
              as |record field|
            >
              <CpPanel @open={{record.isNew}} as |panel|>
                <panel.toggle>
                  {{svgJar
                    (if record.isMainContributor 'star' 'star-outline')
                    class='u-icon'
                  }}
                  {{record.artist.screenname}}
                  ({{record.artist.id}})
                  {{#if record.category}}
                    -
                    {{record.category.name}}
                  {{/if}}
                </panel.toggle>
                <panel.body>
                  <Form @model={{record}} as |subform|>
                    <subform.field
                      @property='category'
                      @type='select-record'
                      @isSearchEnabled={{true}}
                      @options={{this.data.contributorCategories}}
                      @disabled={{field.isDisabled}}
                    />
                    <subform.field
                      @property='isMainContributor'
                      @type='checkbox'
                    />
                  </Form>
                </panel.body>
              </CpPanel>
            </fieldset.field>
          </form.fieldset>

          <form.fieldset @titleKey='products' as |fieldset|>
            <fieldset.field
              @property='products'
              @type='has-many'
              @on={{hash openNewRecord=false}}
              as |record|
            >
              {{record.headline}}
              ({{record.sku}})
              <br />
              {{record.productPhysCategoryId1.name}}
              ({{record.categoryId.name}})
              {{#if record.releaseDate}}
                <br /><strong>{{t
                    'forms.product-family.product.releaseDate'
                  }}</strong>:
                {{record.releaseDate}}
              {{/if}}
              {{#if record.alphaInformationVersion}}
                <br /><strong>{{t
                    'forms.product-family.product.alphaInformationVersion'
                  }}</strong>:
                {{record.alphaInformationVersion}}
              {{/if}}
              {{#if record.alphaInformationCarrierNumber}}
                <br /><strong>{{t
                    'forms.product-family.product.alphaInformationCarrierNumber'
                  }}</strong>:
                {{record.alphaInformationCarrierNumber}}
              {{/if}}
              {{#if record.alphaInformationProductOutfits}}
                <br /><strong>{{t
                    'forms.product-family.product.alphaInformationProductOutfits'
                  }}</strong>:
                {{record.alphaInformationProductOutfits}}
              {{/if}}
              {{#if record.alphaInformationProductOutfitNotes}}
                <br /><strong>{{t
                    'forms.product-family.product.alphaInformationProductOutfitNotes'
                  }}</strong>:
                {{record.alphaInformationProductOutfitNotes}}
              {{/if}}
            </fieldset.field>
          </form.fieldset>

          {{#if @model.mainProduct}}
            <form.fieldset
              @titleKey='inheritedProductDetails'
              @isVisible={{this.isParent}}
            >
              <div class='o-layout'>
                <div class='o-layout__item u-1/1'>
                  {{#if @model.mainProduct.assetId1}}
                    <AssetPreview @asset={{@model.mainProduct.assetId1}} />
                  {{/if}}
                  <br />
                  <h3>
                    {{@model.mainProduct.headline}}
                  </h3>
                  {{! template-lint-disable no-triple-curlies }}
                  {{{@model.mainProduct.text}}}
                  {{! template-lint-enable no-triple-curlies }}
                  <strong>
                    {{t 'forms.product.fields.sku'}}
                  </strong>
                  <br />
                  {{@model.mainProduct.sku}}<br />
                  <br />
                  <strong>
                    {{t 'forms.defaults.fields.label'}}
                  </strong>
                  <br />
                  {{@model.mainProduct.label.displayLabel}}<br />
                  <br />
                  <strong>
                    {{t 'forms.product.fields.releaseDate'}}
                  </strong>
                  <br />
                  {{momentFormat @model.mainProduct.releaseDate 'L'}}
                </div>
              </div>
            </form.fieldset>
          {{/if}}

          <form.fieldset @titleKey='webSeries' as |fieldset|>
            <fieldset.field
              @property='webSeries'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />
          </form.fieldset>

          <form.fieldset @titleKey='subgenres' as |fieldset|>
            <fieldset.field
              @filters={{hash type=(array 'GENRE' 'SUBGENRE')}}
              @property='categories'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />
          </form.fieldset>

          <form.fieldset @titleKey='assignments' as |fieldset|>
            <fieldset.field
              @property='ownedByChannel'
              @type='belongs-to'
              @on={{hash openNewRecord=false openRecord=false}}
            />

            <fieldset.field
              @property='channelsAdditive'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='channelsExcluded'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />

            <fieldset.field
              @property='channelsExclusive'
              @type='has-many'
              @on={{hash openNewRecord=false}}
            />
          </form.fieldset>

          <form.fieldset @titleKey='taxonomies' as |fieldset|>
            <fieldset.field @property='taxa' @type='has-many' />
          </form.fieldset>

          <form.sections.meta />
        </div>
      </div>
    </Form>
  </template>
}
